/**
 * Földgáz naptár segédfüggvények
 * 
 * Gáznap: 06:00 – következő nap 06:00 (24 óra)
 * Gázév: október 1. 06:00 – következő október 1. 06:00
 */

export interface GasDay {
  start: Date;
  end: Date;
  label: string;
}

export interface GasYear {
  start: Date;
  end: Date;
  label: string;
  year: number; // A gázév kezdetének éve (pl. 2026-os gázév = 2026-10-01-től 2027-10-01-ig)
}

const GAS_DAY_START_HOUR = 6;

/**
 * Egy dátumhoz tartozó gáznap kezdetét adja vissza.
 * Ha a dátum 06:00 előtt van, az előző nap 06:00 a gáznap kezdete.
 */
export const getGasDayStart = (date: Date): Date => {
  const result = new Date(date);
  result.setMinutes(0, 0, 0);
  if (result.getHours() < GAS_DAY_START_HOUR) {
    result.setDate(result.getDate() - 1);
  }
  result.setHours(GAS_DAY_START_HOUR);
  return result;
};

/**
 * Egy dátumhoz tartozó gáznap végét adja vissza (a következő nap 06:00).
 */
export const getGasDayEnd = (date: Date): Date => {
  const start = getGasDayStart(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return end;
};

/**
 * Egy dátumhoz tartozó gáznap objektum.
 */
export const getGasDay = (date: Date = new Date()): GasDay => {
  const start = getGasDayStart(date);
  const end = getGasDayEnd(date);
  return {
    start,
    end,
    label: formatGasDay(start)
  };
};

/**
 * Gáznap formázása: YYYY-MM-DD.
 */
export const formatGasDay = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Egy dátumhoz tartozó gázév kezdete.
 */
export const getGasYearStart = (date: Date): Date => {
  const candidate = new Date(date);
  candidate.setHours(6, 0, 0, 0);
  candidate.setMonth(9, 1); // Október 1.
  
  if (date < candidate) {
    candidate.setFullYear(candidate.getFullYear() - 1);
  }
  
  return candidate;
};

/**
 * Egy dátumhoz tartozó gázév vége (következő október 1. 06:00).
 */
export const getGasYearEnd = (date: Date): Date => {
  const start = getGasYearStart(date);
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);
  return end;
};

/**
 * Egy dátumhoz tartozó gázév objektum.
 */
export const getGasYear = (date: Date = new Date()): GasYear => {
  const start = getGasYearStart(date);
  const end = getGasYearEnd(date);
  return {
    start,
    end,
    label: `${start.getFullYear()}/${end.getFullYear()}`,
    year: start.getFullYear()
  };
};

/**
 * Gázév formázása: YYYY/YYYY+1.
 */
export const formatGasYear = (date: Date): string => {
  const start = getGasYearStart(date);
  const end = getGasYearEnd(date);
  return `${start.getFullYear()}/${end.getFullYear()}`;
};

/**
 * Egy dátum tartozik-e a megadott gáznapba.
 */
export const isInGasDay = (date: Date, gasDayStart: Date): boolean => {
  const start = getGasDayStart(gasDayStart);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return date >= start && date < end;
};

/**
 * Egy dátum tartozik-e a megadott gázévbe.
 */
export const isInGasYear = (date: Date, gasYearStart: Date): boolean => {
  const start = getGasYearStart(gasYearStart);
  const end = getGasYearEnd(gasYearStart);
  return date >= start && date < end;
};

/**
 * Gáznapok listája egy időszakban.
 */
export const getGasDaysInRange = (startDate: Date, endDate: Date): GasDay[] => {
  const days: GasDay[] = [];
  let current = getGasDayStart(startDate);
  const last = getGasDayStart(endDate);
  
  while (current <= last) {
    const end = new Date(current);
    end.setDate(end.getDate() + 1);
    days.push({
      start: new Date(current),
      end,
      label: formatGasDay(current)
    });
    current.setDate(current.getDate() + 1);
  }
  
  return days;
};

/**
 * Naptári dátum → gáznap címke (pl. "2026-07-03 gáznap").
 */
export const toGasDayLabel = (date: Date = new Date()): string => {
  const gasDay = getGasDay(date);
  return `${gasDay.label} gáznap`;
};

/**
 * Naptári dátum → gázév címke (pl. "2026/2027 gázév").
 */
export const toGasYearLabel = (date: Date = new Date()): string => {
  const gasYear = getGasYear(date);
  return `${gasYear.label} gázév`;
};
